import { defineStore } from "pinia";

import type { PlanState } from "@/Modules/Plan/Models/Store";
import type { PlanUi } from "@/Modules/Plan/Models/Ui/PlanUi";
import type { Plan } from "@/Modules/Models/Domain/Plan";
import type { PostPlanToPatientApiQuery } from "@/Modules/Models/Api/Plan";
import { EventTestVersion } from "@/Modules/App/TrackersAndCookies/GoogleGTM";
import { StateNamespace } from "@/Modules/App/Models/State/StateNamespace";

import postPlanToPatientAH from "./ActionHandlers/PostPlanToPatient";
import fetchPlansAH from "./ActionHandlers/FetchPlans";
import fetchCurrentPlanAH from "./ActionHandlers/FetchCurrentPlan";

import PlanTransformer from "@/Modules/Transformers/Plan";

import { useUserStore } from "@/Modules/User/Store";
import { useSubscriptionStore } from "@/Modules/Subscription/Store";

import { hasValue, keysOf } from "@/Utils/Objects";

const defaultState: PlanState = {
  plans: [],
  currentPlan: null,
  loading: false,
};

export const usePlanStore = defineStore(StateNamespace.PLAN, {
  state: () => ({ ...defaultState }),
  getters: {
    getPlansUi(state: PlanState): (isPrivate: boolean) => PlanUi[] {
      const plansUi: PlanUi[] = transformAndParsePlans(state.plans);

      return (isPrivate: boolean): PlanUi[] => {
        return plansUi.filter((plan) => {
          if (plan.isActive()) {
            return isPrivate ? plan.isPrivate : !plan.isPrivate;
          }

          return false;
        });
      };
    },

    getPlans(state: PlanState): (isPrivate: boolean) => Plan[] {
      return (isPrivate: boolean): Plan[] => {
        return state.plans.filter((plan) => {
          if (plan.isActive()) {
            return isPrivate ? plan.isPrivate : !plan.isPrivate;
          }

          return false;
        });
      };
    },

    getNextPlan(state: PlanState): Plan | undefined {
      if (hasValue(state.currentPlan)) {
        if (state.currentPlan.isPromo) {
          return this.plans.find((plan) => plan.promoPlanUuid === state.currentPlan?.id);
        }
      }
    },

    getPlanUiById(state: PlanState): (planId: string) => PlanUi | undefined {
      const plansUi: PlanUi[] = transformAndParsePlans(state.plans);

      return (planId: string): PlanUi | undefined => plansUi.find((plan) => plan.id === planId);
    },

    getPlanById(state: PlanState): (planId: string) => Plan | undefined {
      return (planId: string): Plan | undefined => state.plans.find((plan) => plan.id === planId);
    },

    getTestVersion(): EventTestVersion | undefined {
      const numPlans = this.getPlans(false).length;

      return numPlans === 2
        ? EventTestVersion.VERSION_A
        : numPlans === 3
          ? EventTestVersion.VERSION_B
          : undefined;
    },
  },
  actions: {
    clear() {
      keysOf(defaultState).forEach((k) => {
        // @ts-expect-error clear store
        this[k] = defaultState[k];
      });
    },

    fetchPlans(comeFromOnboarding: boolean = false) {
      const userStore = useUserStore();

      if (!hasValue(userStore.user)) return;

      return fetchPlansAH(userStore.user.id, comeFromOnboarding);
    },

    setCurrentPlan(planId: string) {
      return fetchCurrentPlanAH(planId);
    },

    postPlanToPatient(infoPostPlan: PostPlanToPatientApiQuery) {
      return postPlanToPatientAH(infoPostPlan);
    },
  },
});

const transformAndParsePlans = (plans: Plan[]): PlanUi[] => {
  const subscriptionStore = useSubscriptionStore();

  const plansUi: PlanUi[] = plans.map((auxPlan) => {
    const plan = PlanTransformer.toUi(auxPlan);

    if (plan.id === subscriptionStore.getNextPlanUuid) {
      return {
        ...plan,
        subscribeText: "plan.card.subscribe_text.current_plan",
      };
    } else {
      return plan;
    }
  });

  return plansUi;
};

export type PlanStore = ReturnType<typeof usePlanStore>;

export default usePlanStore;
